"use client";

import { useMemo, useState } from "react";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  collection,
  addDoc,
  Timestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import getApp from "@/lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { parseInviteInput } from "@/lib/inviteParser";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { AddPetModal } from "@/components/features/AddPetModal";
import { HouseholdInviteSheet } from "@/components/features/HouseholdInviteSheet";
import { prepareLeaveHousehold } from "@/lib/leaveHousehold";
import { userFacingMessage } from "@/lib/errors/userFacingError";
import type { Household, MemberProfile } from "@/lib/models";

/**
 * Wave N integration:
 *  - N.1a adds the optional `showLeaveAction` prop + Leave Household flow
 *    (rendered on the dedicated `/account/households` route).
 *  - N.1b adds the per-household QR invite button + HouseholdInviteSheet.
 *  - N.3 adds the Pets section + Add Pet modal at the bottom of the panel.
 *
 * The Pets list is derived by filtering `members` (from HouseholdContext)
 * down to `memberType === "pet"`.
 */

interface HouseholdManagementProps {
  /**
   * When true, render the in-list "Leave" action. The dedicated
   * `/account/households` route opts in; the embedded view on `/account`
   * leaves it off to keep the embedded UI compact.
   */
  showLeaveAction?: boolean;
}

export function HouseholdManagement({
  showLeaveAction = false,
}: HouseholdManagementProps) {
  const t = useTranslations();
  const {
    profile,
    householdId,
    allHouseholds,
    members,
    switchHousehold,
  } = useHouseholdContext();
  const [joinId, setJoinId] = useState("");
  const [createName, setCreateName] = useState("");
  const [joining, setJoining] = useState(false);
  const [creating, setCreating] = useState(false);
  const [leavingId, setLeavingId] = useState<string | null>(null);
  const [pendingLeave, setPendingLeave] = useState<Household | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Wave N.3: pet UI state.
  const [showAddPet, setShowAddPet] = useState(false);
  const [petError, setPetError] = useState<string | null>(null);

  const pets = useMemo<MemberProfile[]>(
    () => members.filter((m) => m.memberType === "pet"),
    [members],
  );

  // Wave N.1b: QR invite sheet state. We track the household being invited
  // into so the sheet has both id + label for the share copy.
  const [inviteSheet, setInviteSheet] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!joinId.trim() || !profile?.uid) return;

    setJoining(true);
    setError(null);
    setSuccess(null);

    try {
      // Route every join through the `resolveInvite` Cloud Function so
      // role assignment + token validation happens server-side. Mirrors
      // iOS `OnboardingGateView` and the existing /join landing page.
      // Web previously self-joined via raw householdId arrayUnion, which
      // bypassed createInvite token gating entirely.
      const parsed = parseInviteInput(joinId);
      if (parsed.kind === "invalid") {
        setError(t("households.join.errorInvalidInvite"));
        setJoining(false);
        return;
      }
      if (parsed.kind === "hh_id") {
        setError(t("households.join.errorOldFormat"));
        setJoining(false);
        return;
      }

      const functions = getFunctions(getApp());
      const resolveInvite = httpsCallable<
        { token: string },
        {
          success?: boolean;
          alreadyMember?: boolean;
          householdId?: string;
        }
      >(functions, "resolveInvite");
      const result = await resolveInvite({ token: parsed.token });
      const data = result.data;

      if (data.alreadyMember) {
        setError(t("households.join.alreadyMember"));
        setJoining(false);
        return;
      }

      if (data.householdId) {
        setSuccess(t("households.join.success"));
        setJoinId("");
        switchHousehold(data.householdId);
      }
    } catch (err) {
      console.error("Error joining household:", err);
      setError(userFacingMessage(err, t("households.join.error")));
    } finally {
      setJoining(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim() || !profile?.uid) return;

    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const newHousehold: Partial<Household> & Record<string, unknown> = {
        household_label: createName.trim(),
        household_owner: profile.uid,
        household_members: [profile.uid],
        chores: [],
      };

      const ref = collection(db, "households");
      const docRef = await addDoc(ref, {
        ...newHousehold,
        created_at: Timestamp.now(),
      });

      // Add to profile's households
      const profileRef = doc(db, "member_profiles", profile.uid);
      await updateDoc(profileRef, {
        households: arrayUnion(docRef.id),
      });

      setSuccess(t("households.create.success"));
      setCreateName("");
      switchHousehold(docRef.id);
    } catch (err) {
      console.error("Error creating household:", err);
      setError(t("households.create.error"));
    } finally {
      setCreating(false);
    }
  };

  /**
   * Apply the leave operation to Firestore. Splits the pure guard logic
   * (in `lib/leaveHousehold.ts`) from the IO so the helper stays unit
   * testable. The guard has already passed — this only writes.
   */
  const performLeave = async (hh: Household) => {
    if (!hh.uid || !profile?.uid) return;
    setLeavingId(hh.uid);
    setError(null);
    setSuccess(null);
    try {
      const hhRef = doc(db, "households", hh.uid);
      await updateDoc(hhRef, {
        household_members: arrayRemove(profile.uid),
      });

      const profileRef = doc(db, "member_profiles", profile.uid);
      await updateDoc(profileRef, {
        households: arrayRemove(hh.uid),
      });

      setSuccess(t("households.leave.success"));
    } catch (err) {
      console.error("Error leaving household:", err);
      setError(t("households.leave.error"));
    } finally {
      setLeavingId(null);
      setPendingLeave(null);
    }
  };

  const requestLeave = (hh: Household) => {
    if (!hh.uid || !profile?.uid) return;

    const result = prepareLeaveHousehold({
      household: hh,
      householdId: hh.uid,
      userId: profile.uid,
      activeHouseholdId: householdId,
    });

    setError(null);
    setSuccess(null);

    if (result.kind === "blocked") {
      switch (result.reason) {
        case "active-household":
          setError(t("households.leave.cannotActive"));
          return;
        case "last-admin":
          setError(t("households.leave.cannotLastAdmin"));
          return;
        case "not-a-member":
          setError(t("households.leave.notAMember"));
          return;
      }
    }

    // Allowed — open the confirmation prompt.
    setPendingLeave(hh);
  };

  return (
    <div className="space-y-4">
      {/* Household List */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-500">
          {t("households.list.title")}
        </p>
        {allHouseholds.length === 0 ? (
          <p className="text-sm text-gray-400">{t("households.list.empty")}</p>
        ) : (
          <Card padding="p-0">
            <div className="divide-y divide-gray-100 dark:divide-white/5">
              {allHouseholds.map((hh) => {
                const isActive = hh.uid === householdId;
                const isLeaving = leavingId === hh.uid;
                const label = hh.household_label || t("households.unnamed");
                return (
                  // Wave N integration: row is a flex <div> with three slots —
                  // QR invite icon (N.1b) on the left, switch-household button
                  // in the middle, optional Leave action (N.1a) on the right.
                  <div
                    key={hh.uid}
                    className="flex items-center gap-2 p-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    {/* QR invite icon — opens the per-household invite sheet (N.1b). */}
                    <button
                      type="button"
                      onClick={() =>
                        hh.uid && setInviteSheet({ id: hh.uid, label })
                      }
                      aria-label={t("households.invite.openAria", {
                        household: label,
                      })}
                      className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-white/10 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                        <path d="M14 14h3v3" />
                        <path d="M20 14v3" />
                        <path d="M14 20h3" />
                        <path d="M20 20v.01" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => hh.uid && switchHousehold(hh.uid)}
                      className="flex-1 flex items-center justify-between text-left min-w-0"
                    >
                      <span className="text-sm font-medium truncate">
                        {label}
                      </span>
                      {isActive && (
                        <span
                          className="ml-2 shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full text-white"
                          style={{
                            backgroundColor: "var(--accent-color, #00C7BE)",
                          }}
                        >
                          {t("households.activeBadge")}
                        </span>
                      )}
                    </button>
                    {/* Leave Household action (Wave N.1a) — only shown on the dedicated route. */}
                    {showLeaveAction && (
                      <button
                        onClick={() => requestLeave(hh)}
                        disabled={isLeaving}
                        className="text-xs font-medium px-2 py-1 rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 shrink-0"
                        aria-label={t("households.leave.button")}
                      >
                        {isLeaving
                          ? t("households.leave.leaving")
                          : t("households.leave.button")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Status Messages */}
      {error && <p className="text-sm text-red-500 px-1">{error}</p>}
      {success && (
        <p
          className="text-sm px-1"
          style={{ color: "var(--accent-color, #00C7BE)" }}
        >
          {success}
        </p>
      )}

      {/* Join Household */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-500">
          {t("households.join.title")}
        </p>
        <Card>
          <form onSubmit={handleJoin} className="flex gap-2">
            <input
              type="text"
              value={joinId}
              onChange={(e) => setJoinId(e.target.value)}
              placeholder={t("households.join.placeholder")}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
            <button
              type="submit"
              disabled={joining || !joinId.trim()}
              className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 shrink-0"
              style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
            >
              {joining ? "..." : t("households.join.button")}
            </button>
          </form>
        </Card>
      </div>

      {/* Create Household */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-500">
          {t("households.create.title")}
        </p>
        <Card>
          <form onSubmit={handleCreate} className="flex gap-2">
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder={t("households.create.placeholder")}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
            <button
              type="submit"
              disabled={creating || !createName.trim()}
              className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 shrink-0"
              style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
            >
              {creating ? "..." : t("households.create.button")}
            </button>
          </form>
        </Card>
      </div>

      {/*
        Wave N.3: Pets section. Renders the household's pets (filtered out
        of `members` by memberType) and exposes an "Add Pet" CTA that
        opens the AddPetModal. Sibling waves N.1a/N.1b are also editing
        this file to add a People/Members section above; we deliberately
        sit at the bottom so the merge surface is small.
      */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-500">{t("pets.section.title")}</p>
          {householdId && (
            <button
              type="button"
              onClick={() => setShowAddPet(true)}
              className="text-xs font-semibold px-3 py-1 rounded-full text-white"
              style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
            >
              {t("pets.add.button")}
            </button>
          )}
        </div>
        {petError && <p className="text-sm text-red-500 px-1">{petError}</p>}
        {pets.length === 0 ? (
          <p className="text-sm text-gray-400 px-1">{t("pets.section.empty")}</p>
        ) : (
          <Card padding="p-0">
            <div className="divide-y divide-gray-100 dark:divide-white/5">
              {pets.map((pet) => {
                const displayName = pet.nickname || pet.name || "Pet";
                const speciesKey = pet.details?.species;
                const breed = pet.details?.breed;
                const speciesLabel = speciesKey
                  ? t(`pets.species.${speciesKey}`)
                  : null;
                const subtitleParts: string[] = [];
                if (speciesLabel) subtitleParts.push(speciesLabel);
                if (breed) subtitleParts.push(breed);
                return (
                  <div
                    key={pet.uid}
                    className="flex items-center gap-3 p-3"
                  >
                    <Avatar
                      src={pet.profile_url}
                      alt={displayName}
                      size={36}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight truncate">
                        {displayName}
                      </p>
                      {subtitleParts.length > 0 && (
                        <p className="text-xs text-gray-500 truncate">
                          {subtitleParts.join(" • ")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {householdId && profile?.uid && (
        <AddPetModal
          isOpen={showAddPet}
          onClose={() => setShowAddPet(false)}
          householdId={householdId}
          createdBy={profile.uid}
          onError={(msg) => setPetError(msg)}
        />
      )}

      {/* Leave Confirmation Modal (Wave N.1a) */}
      {pendingLeave && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPendingLeave(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold">
              {t("households.leave.confirmTitle")}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t("households.leave.confirmBody", {
                name:
                  pendingLeave.household_label || t("households.unnamed"),
              })}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setPendingLeave(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10"
              >
                {t("households.leave.cancel")}
              </button>
              <button
                onClick={() => performLeave(pendingLeave)}
                disabled={leavingId !== null}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {leavingId
                  ? t("households.leave.leaving")
                  : t("households.leave.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wave N.1b: per-household QR invite sheet (mounted once, opened per row). */}
      {inviteSheet && (
        <HouseholdInviteSheet
          householdId={inviteSheet.id}
          householdName={inviteSheet.label}
          onClose={() => setInviteSheet(null)}
        />
      )}
    </div>
  );
}
